import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '../context/AuthContext';
import { usePortalConfig } from '@shared/config';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { Label } from '@shared/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@shared/components/ui/card';
import { Alert, AlertDescription } from '@shared/components/ui/alert';
import { Loader2, CheckCircle, Check } from 'lucide-react';

const registerSchema = z.object({
  email: z.string().email('Voer een geldig e-mailadres in'),
  password: z.string()
    .min(6, 'Wachtwoord moet minimaal 6 tekens bevatten')
    .regex(/[A-Z]/, 'Wachtwoord moet minimaal één hoofdletter bevatten')
    .regex(/[0-9]/, 'Wachtwoord moet minimaal één cijfer bevatten'),
  confirmPassword: z.string(),
  fullName: z.string().min(2, 'Naam moet minimaal 2 tekens bevatten'),
  organization: z.string().min(2, 'Organisatie moet minimaal 2 tekens bevatten'),
  functie: z.string().min(2, 'Functie moet minimaal 2 tekens bevatten'),
  telefoonnummer: z.string()
    .min(10, 'Telefoonnummer moet minimaal 10 cijfers bevatten')
    .regex(/^[+]?[0-9\s-()]+$/, 'Voer een geldig telefoonnummer in'),
  newsletterPreference: z.enum(['casefinder', 'casefinder_hr', 'casefinder_hr_daily']),
}).refine((data) => data.password === data.confirmPassword, {
  message: 'Wachtwoorden komen niet overeen',
  path: ['confirmPassword'],
});

type RegisterFormData = z.infer<typeof registerSchema>;

interface RegisterFormProps {
  onSuccess?: () => void;
}

export function RegisterForm({ onSuccess }: RegisterFormProps) {
  const navigate = useNavigate();
  const { signUp, error, clearError } = useAuth();
  const { branding, id, newsletter } = usePortalConfig();

  const isLexzone = id === 'lexzone';
  const isLexwoo = id === 'lexwoo';
  const isRaadVanState = isLexzone || isLexwoo;
  const checkIconClass = isLexzone || isLexwoo ? 'text-primary' : 'text-green-600 dark:text-green-400';
  const weeklyTime = `Elke ${newsletter.weeklySendDay} om ${newsletter.weeklySendTime}`;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [selectedTier, setSelectedTier] = useState<'casefinder' | 'casefinder_hr' | 'casefinder_hr_daily' | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [existingAccountEmail, setExistingAccountEmail] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      newsletterPreference: 'casefinder_hr',
    },
  });

  const handleTierSelection = (tier: 'casefinder' | 'casefinder_hr' | 'casefinder_hr_daily') => {
    setSelectedTier(tier);
    setValue('newsletterPreference', tier);
    setShowForm(true);
  };

  const onSubmit = async (data: RegisterFormData) => {
    try {
      setIsSubmitting(true);
      clearError();

      // Register user
      await signUp(data.email, data.password, {
        fullName: data.fullName,
        organization: data.organization,
        functie: data.functie,
        telefoonnummer: data.telefoonnummer,
        newsletterPreference: data.newsletterPreference,
      });

      // Show success immediately
      // Newsletter subscriptions are now handled by database trigger (handle_newsletter_subscription)
      setRegistrationSuccess(true);

      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      // Check if error is "User already registered"
      const error = err instanceof Error ? err : new Error('Unknown error');
      const errorMessage = error.message;
      if (
        errorMessage.toLowerCase().includes('already registered') ||
        errorMessage.toLowerCase().includes('already exists') ||
        errorMessage.toLowerCase().includes('user already registered')
      ) {
        setExistingAccountEmail(data.email);
      }
      // Error is already set in context
    } finally {
      setIsSubmitting(false);
    }
  };

  if (registrationSuccess) {
    return (
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle className={`h-5 w-5 ${checkIconClass}`} />
            Registratie succesvol
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Uw account is aangemaakt. U kunt nu inloggen.
          </p>
        </CardContent>
        <CardFooter>
          <Button
            onClick={() => navigate('/login')}
            className="w-full"
          >
            Naar inloggen
          </Button>
        </CardFooter>
      </Card>
    );
  }

  // Show package selection first
  if (!showForm) {
    return (
      <Card className="w-full max-w-5xl">
        <CardHeader>
          <CardTitle className="text-center">Kies uw pakket</CardTitle>
          <CardDescription className="text-center">
            Selecteer welke diensten u wilt gebruiken. U kunt dit later altijd nog wijzigen in uw instellingen.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Casefinder + HR Newsletter */}
            <div
              className={`relative p-6 border-2 rounded-lg transition-all cursor-pointer hover:border-primary/50 border-muted`}
              onClick={() => handleTierSelection('casefinder_hr')}
            >
              <div className="text-center mb-4">
                <h4 className="text-lg font-bold mb-1">Wekelijks up to date</h4>
                <p className="text-xs text-muted-foreground">{isRaadVanState ? 'Toegang + wekelijkse RvS nieuwsbrief' : 'Toegang + wekelijkse Hoge Raad nieuwsbrief'}</p>
              </div>
              <Button
                type="button"
                variant="outline"
                className="w-full mb-4"
                onClick={(e) => {
                  e.stopPropagation();
                  handleTierSelection('casefinder_hr');
                }}
              >
                Selecteer
              </Button>
              <div className="space-y-2">
                <div className="flex items-start gap-2 text-sm">
                  <Check className={`h-4 w-4 ${checkIconClass} mt-0.5 flex-shrink-0`} />
                  <span>Alles van {branding.name}</span>
                </div>
                <div className="flex items-start gap-2 text-sm">
                  <Check className={`h-4 w-4 ${checkIconClass} mt-0.5 flex-shrink-0`} />
                  <span>{isRaadVanState ? 'Wekelijkse RvS nieuwsbrief' : 'Wekelijkse Hoge Raad nieuwsbrief'}</span>
                </div>
                <div className="flex items-start gap-2 text-sm">
                  <Check className={`h-4 w-4 ${checkIconClass} mt-0.5 flex-shrink-0`} />
                  <span>{weeklyTime}</span>
                </div>
                <div className="flex items-start gap-2 text-sm">
                  <Check className={`h-4 w-4 ${checkIconClass} mt-0.5 flex-shrink-0`} />
                  <span>Belangrijkste uitspraken</span>
                </div>
              </div>
            </div>

            {/* Casefinder + HR + Daily */}
            <div
              className={`relative p-6 border-2 rounded-lg transition-all cursor-pointer hover:border-primary/50 border-muted`}
              onClick={() => handleTierSelection('casefinder_hr_daily')}
            >
              <div className="text-center mb-4">
                <h4 className="text-lg font-bold mb-1">Dagelijks up to date</h4>
                <p className="text-xs text-muted-foreground">Toegang + dagelijkse nieuwsbrief</p>
              </div>
              <Button
                type="button"
                variant="outline"
                className="w-full mb-4"
                onClick={(e) => {
                  e.stopPropagation();
                  handleTierSelection('casefinder_hr_daily');
                }}
              >
                Selecteer
              </Button>
              <div className="space-y-2">
                <div className="flex items-start gap-2 text-sm">
                  <Check className={`h-4 w-4 ${checkIconClass} mt-0.5 flex-shrink-0`} />
                  <span>Alles van {branding.name} + {isRaadVanState ? 'RvS' : 'HR'} nieuwsbrief</span>
                </div>
                <div className="flex items-start gap-2 text-sm">
                  <Check className={`h-4 w-4 ${checkIconClass} mt-0.5 flex-shrink-0`} />
                  <span>Dagelijkse nieuwsbrief</span>
                </div>
                <div className="flex items-start gap-2 text-sm">
                  <Check className={`h-4 w-4 ${checkIconClass} mt-0.5 flex-shrink-0`} />
                  <span>Elke dag om {newsletter.dailySendTime}</span>
                </div>
                <div className="flex items-start gap-2 text-sm">
                  <Check className={`h-4 w-4 ${checkIconClass} mt-0.5 flex-shrink-0`} />
                  <span>Alle nieuwe uitspraken</span>
                </div>
                <div className="flex items-start gap-2 text-sm">
                  <Check className={`h-4 w-4 ${checkIconClass} mt-0.5 flex-shrink-0`} />
                  <span>Blijf volledig up-to-date</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
        <CardFooter>
          <p className="text-sm text-muted-foreground text-center w-full">
            Heeft u al een account?{' '}
            <Link
              to="/login"
              className="text-primary hover:underline"
            >
              Log hier in
            </Link>
          </p>
        </CardFooter>
      </Card>
    );
  }

  // Show registration form after package selection
  return (
    <Card className="w-full max-w-xl">
      <CardHeader>
        <CardTitle>Account aanmaken</CardTitle>
        <CardDescription>
          Vul uw gegevens in om uw account aan te maken
        </CardDescription>
      </CardHeader>
      <form onSubmit={handleSubmit(onSubmit)}>
        <CardContent className="space-y-4">
          {error && !existingAccountEmail && (
            <Alert variant="destructive">
              <AlertDescription>{error.message}</AlertDescription>
            </Alert>
          )}

          {/* Show selected package */}
          <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Geselecteerd pakket:</p>
                <p className="text-lg font-bold">
                  {selectedTier === 'casefinder' && branding.name}
                  {selectedTier === 'casefinder_hr' && `${branding.name} + ${isRaadVanState ? 'RvS' : 'HR'}`}
                  {selectedTier === 'casefinder_hr_daily' && 'Complete Suite'}
                </p>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowForm(false)}
              >
                Wijzig pakket
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="fullName">Volledige naam *</Label>
            <Input
              id="fullName"
              type="text"
              placeholder="Jan Jansen"
              {...register('fullName')}
              disabled={isSubmitting}
              required
            />
            {errors.fullName && (
              <p className="text-sm text-destructive">{errors.fullName.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="organization">Organisatie *</Label>
            <Input
              id="organization"
              type="text"
              placeholder="Advocatenkantoor XYZ"
              {...register('organization')}
              disabled={isSubmitting}
              required
            />
            {errors.organization && (
              <p className="text-sm text-destructive">{errors.organization.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="functie">Functie *</Label>
            <Input
              id="functie"
              type="text"
              placeholder="Advocaat / Jurist"
              {...register('functie')}
              disabled={isSubmitting}
              required
            />
            {errors.functie && (
              <p className="text-sm text-destructive">{errors.functie.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="telefoonnummer">Telefoonnummer *</Label>
            <Input
              id="telefoonnummer"
              type="tel"
              placeholder="+31 6 12345678"
              {...register('telefoonnummer')}
              disabled={isSubmitting}
              required
            />
            {errors.telefoonnummer && (
              <p className="text-sm text-destructive">{errors.telefoonnummer.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">E-mailadres *</Label>
            <Input
              id="email"
              type="email"
              placeholder="naam@voorbeeld.nl"
              {...register('email')}
              disabled={isSubmitting}
            />
            {errors.email && (
              <p className="text-sm text-destructive">{errors.email.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Wachtwoord *</Label>
            <Input
              id="password"
              type="password"
              {...register('password')}
              disabled={isSubmitting}
            />
            {errors.password && (
              <p className="text-sm text-destructive">{errors.password.message}</p>
            )}
            <p className="text-xs text-muted-foreground">
              Minimaal 6 tekens, één hoofdletter en één cijfer
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Bevestig wachtwoord *</Label>
            <Input
              id="confirmPassword"
              type="password"
              {...register('confirmPassword')}
              disabled={isSubmitting}
            />
            {errors.confirmPassword && (
              <p className="text-sm text-destructive">{errors.confirmPassword.message}</p>
            )}
          </div>
        </CardContent>
        <CardFooter className="flex flex-col space-y-4 pt-6">
          <Button
            type="submit"
            className="w-full"
            disabled={isSubmitting}
          >
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isSubmitting ? 'Account aanmaken...' : 'Registreren'}
          </Button>

          {existingAccountEmail && (
            <Alert>
              <AlertDescription>
                Je hebt al een account op een van onze portals.{' '}
                <Link
                  to="/login"
                  state={{ email: existingAccountEmail }}
                  className="font-medium underline"
                >
                  Log in om je ook aan te melden voor {branding.name}
                </Link>
              </AlertDescription>
            </Alert>
          )}

          <p className="text-sm text-muted-foreground text-center">
            Heeft u al een account?{' '}
            <Link
              to="/login"
              className="text-primary hover:underline"
            >
              Log hier in
            </Link>
          </p>
        </CardFooter>
      </form>
    </Card>
  );
}